import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { AlertCircle, Calendar, CheckCircle2, Loader2, MapPin, RefreshCcw } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';

export const Route = createFileRoute('/customer/site-visit-approval/$token')({
  component: SiteVisitApprovalPage,
});

interface SiteVisitDetails {
  name: string | null;
  address: string | null;
  scheduledAt: string | null;
  visitType: string;
  notes: string | null;
}

async function fetchSiteVisit(token: string): Promise<SiteVisitDetails> {
  const res = await fetch(`/api/v1/site-visits/approval/${token}`);
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    const e = new Error(err.message ?? 'Request failed');
    (e as Error & { status: number }).status = res.status;
    throw e;
  }
  return res.json() as Promise<SiteVisitDetails>;
}

async function approveSiteVisit(token: string): Promise<void> {
  const res = await fetch(`/api/v1/site-visits/approve/${token}`, { method: 'POST' });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    const e = new Error(err.message ?? 'Request failed');
    (e as Error & { status: number }).status = res.status;
    throw e;
  }
}

async function rescheduleSiteVisit(
  token: string,
  payload: { rescheduledDate: string; rescheduleRemarks?: string },
): Promise<void> {
  const res = await fetch(`/api/v1/site-visits/reschedule/${token}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as { message?: string };
    const e = new Error(err.message ?? 'Request failed');
    (e as Error & { status: number }).status = res.status;
    throw e;
  }
}

function formatVisitType(type: string) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function SiteVisitApprovalPage() {
  const { token } = Route.useParams();
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduledDate, setRescheduledDate] = useState('');
  const [rescheduleRemarks, setRescheduleRemarks] = useState('');
  const [confirmed, setConfirmed] = useState<'approved' | 'rescheduled' | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['site-visit-approval', token],
    queryFn: () => fetchSiteVisit(token),
    retry: false,
  });

  const approveMutation = useMutation({
    mutationFn: () => approveSiteVisit(token),
    onSuccess: () => setConfirmed('approved'),
  });

  const rescheduleMutation = useMutation({
    mutationFn: () =>
      rescheduleSiteVisit(token, {
        rescheduledDate,
        rescheduleRemarks: rescheduleRemarks || undefined,
      }),
    onSuccess: () => setConfirmed('rescheduled'),
  });

  const errorStatus = (error as (Error & { status?: number }) | null)?.status;

  if (isLoading) {
    return (
      <PublicShell>
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </PublicShell>
    );
  }

  if (errorStatus === 410) {
    return (
      <PublicShell>
        <ErrorState
          icon={<AlertCircle className="h-10 w-10 text-amber-400" />}
          title="Link Expired"
          message="This approval link has expired. Please contact Beeah to request a new one."
        />
      </PublicShell>
    );
  }

  if (errorStatus === 409 || (!data && error)) {
    return (
      <PublicShell>
        <ErrorState
          icon={<CheckCircle2 className="h-10 w-10 text-green-400" />}
          title="Already Responded"
          message="This site visit has already been approved or rescheduled."
        />
      </PublicShell>
    );
  }

  if (!data) {
    return (
      <PublicShell>
        <ErrorState
          icon={<AlertCircle className="h-10 w-10 text-red-400" />}
          title="Not Found"
          message="We couldn't find this site visit. Please check the link in your email."
        />
      </PublicShell>
    );
  }

  if (confirmed === 'approved') {
    return (
      <PublicShell>
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Site Visit Confirmed</h2>
            <p className="mt-1 text-sm text-gray-500">
              Thank you. The Beeah team has been notified and will proceed with the visit as
              scheduled.
            </p>
          </div>
        </div>
      </PublicShell>
    );
  }

  if (confirmed === 'rescheduled') {
    return (
      <PublicShell>
        <div className="flex flex-col items-center gap-4 py-6 text-center">
          <RefreshCcw className="h-12 w-12 text-blue-500" />
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Reschedule Request Sent</h2>
            <p className="mt-1 text-sm text-gray-500">
              Your reschedule request has been submitted. The Beeah team will contact you to confirm
              a new date.
            </p>
          </div>
        </div>
      </PublicShell>
    );
  }

  const scheduledDate = data.scheduledAt
    ? new Date(data.scheduledAt).toLocaleString('en-AE', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'To be confirmed';

  return (
    <PublicShell>
      <div className="space-y-5">
        <div>
          <h2 className="text-base font-semibold text-gray-800">
            {data.name ?? 'Site Visit Details'}
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">{formatVisitType(data.visitType)}</p>
        </div>

        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 text-sm">
          <div className="flex items-start gap-3 px-4 py-3">
            <Calendar className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
            <div>
              <p className="font-medium text-gray-700">Scheduled Date</p>
              <p className="text-gray-500">{scheduledDate}</p>
            </div>
          </div>
          {data.address && (
            <div className="flex items-start gap-3 px-4 py-3">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <div>
                <p className="font-medium text-gray-700">Location</p>
                <p className="text-gray-500">{data.address}</p>
              </div>
            </div>
          )}
          {data.notes && (
            <div className="px-4 py-3">
              <p className="font-medium text-gray-700 text-sm">Notes</p>
              <p className="text-gray-500 text-sm mt-0.5">{data.notes}</p>
            </div>
          )}
        </div>

        {!showReschedule ? (
          <div className="flex flex-col gap-3">
            <Button
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate()}
            >
              {approveMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Confirming…
                </span>
              ) : (
                'Approve Site Visit'
              )}
            </Button>
            <Button variant="outline" className="w-full" onClick={() => setShowReschedule(true)}>
              Reschedule Site Visit
            </Button>
            {approveMutation.isError && (
              <p className="text-center text-sm text-red-600">
                Something went wrong. Please try again.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700">Request Reschedule</h3>
            <div className="space-y-1">
              <label className="block text-xs font-medium text-gray-600" htmlFor="reschedule-date">
                Preferred Date & Time
              </label>
              <input
                id="reschedule-date"
                type="datetime-local"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                value={rescheduledDate}
                onChange={(e) => setRescheduledDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label
                className="block text-xs font-medium text-gray-600"
                htmlFor="reschedule-remarks"
              >
                Remarks (optional)
              </label>
              <textarea
                id="reschedule-remarks"
                rows={3}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-none"
                placeholder="Reason for reschedule or preferred time slots…"
                value={rescheduleRemarks}
                onChange={(e) => setRescheduleRemarks(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowReschedule(false)}
                disabled={rescheduleMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={!rescheduledDate || rescheduleMutation.isPending}
                onClick={() => rescheduleMutation.mutate()}
              >
                {rescheduleMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                  </span>
                ) : (
                  'Submit Request'
                )}
              </Button>
            </div>
            {rescheduleMutation.isError && (
              <p className="text-center text-sm text-red-600">
                Something went wrong. Please try again.
              </p>
            )}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-[#1a5276] px-6 py-5 text-center">
          <p className="text-white font-bold text-lg">Beeah Group</p>
          <p className="text-[#aed6f1] text-xs mt-1">re.life Study Portal</p>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </main>
  );
}

function ErrorState({
  icon,
  title,
  message,
}: {
  icon: React.ReactNode;
  title: string;
  message: string;
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      {icon}
      <div>
        <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
        <p className="mt-1 text-sm text-gray-500">{message}</p>
      </div>
    </div>
  );
}
